import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Package, Search, Truck, CheckCircle2, Clock, AlertCircle } from "lucide-react";

const STATUS_META = {
  paid: { label: "Payment confirmed", icon: CheckCircle2, color: "text-[#00B5D8]" },
  pending: { label: "Awaiting payment", icon: Clock, color: "text-amber-300" },
  shipped: { label: "Shipped", icon: Truck, color: "text-green-400" },
  delivered: { label: "Delivered", icon: CheckCircle2, color: "text-green-400" },
  cancelled: { label: "Cancelled", icon: AlertCircle, color: "text-red-400" },
};

export default function OrderTrack() {
  const [params] = useSearchParams();
  const [code, setCode] = useState((params.get("code") || "").toUpperCase());
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const lookup = async (e) => {
    if (e) e.preventDefault();
    const c = code.trim();
    if (!c) return;
    setLoading(true); setError(""); setOrder(null);
    try {
      const { data } = await api.get(`/orders/track/${encodeURIComponent(c)}`);
      setOrder(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not find that order.");
    } finally {
      setLoading(false);
    }
  };

  const shipped = order && (order.status === "shipped" || order.status === "delivered");
  const meta = order ? (STATUS_META[shipped ? order.status : (order.payment_status || order.status)] || STATUS_META.pending) : null;
  const StatusIcon = meta?.icon || Clock;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white pt-32 pb-24 px-6">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <Package size={28} className="text-[#00B5D8]" />
          <h1 className="font-display text-4xl sm:text-5xl">Track your order</h1>
        </div>
        <p className="text-white/60 mb-8">Enter the order code from your confirmation email (e.g. <span className="font-mono text-[#00B5D8]">PR-7K3M9</span>).</p>

        <form onSubmit={lookup} className="flex gap-2">
          <Input
            data-testid="track-code-input"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="PR-XXXXX"
            className="bg-white/5 border-white/10 text-white placeholder:text-white/40 font-mono"
          />
          <Button type="submit" data-testid="track-submit" disabled={loading}
            className="bg-[#00B5D8] hover:bg-[#009AB8] text-black font-bold rounded-full px-6">
            <Search size={16} className="mr-1" /> {loading ? "…" : "Track"}
          </Button>
        </form>

        {error && (
          <p data-testid="track-error" className="mt-6 flex items-center gap-2 text-red-300 text-sm">
            <AlertCircle size={16} /> {error}
          </p>
        )}

        {order && (
          <div data-testid="track-result" className="mt-8 rounded-2xl border border-white/10 bg-white/5 p-6">
            <div className="flex items-center justify-between">
              <span className="font-mono text-lg font-bold text-[#00B5D8]" data-testid="track-order-no">{order.order_no}</span>
              <span className={`flex items-center gap-2 text-sm font-semibold ${meta.color}`} data-testid="track-status">
                <StatusIcon size={18} /> {meta.label}
              </span>
            </div>

            <div className="mt-5 space-y-2">
              {order.items?.map((it, i) => (
                <div key={i} className="flex justify-between text-sm text-white/70">
                  <span>{it.name}</span><span>× {it.quantity}</span>
                </div>
              ))}
            </div>

            {order.live && (order.live.status || order.live.detail) && (
              <div className="mt-5 pt-4 border-t border-white/10" data-testid="track-live">
                <p className="text-white/50 text-xs uppercase tracking-widest mb-2">Live carrier status</p>
                <p className="text-sm text-white">
                  <span className="font-semibold text-green-400">{order.live.status || "In transit"}</span>
                  {order.live.detail ? ` — ${order.live.detail}` : ""}
                </p>
                {order.live.location && <p className="text-xs text-white/50 mt-1">{order.live.location}</p>}
                {order.live.eta && <p className="text-xs text-white/50 mt-1">Estimated delivery: {new Date(order.live.eta).toLocaleDateString()}</p>}
              </div>
            )}

            {order.tracking_number && (
              <div className="mt-5 pt-4 border-t border-white/10 text-sm">
                <p className="text-white/50">DHL tracking</p>
                {order.tracking_url
                  ? <a href={order.tracking_url} target="_blank" rel="noopener noreferrer" data-testid="track-dhl-link" className="font-mono text-[#00B5D8] hover:underline">{order.tracking_number}</a>
                  : <span className="font-mono text-white">{order.tracking_number}</span>}
              </div>
            )}

            {order.created_at && (
              <p className="mt-5 text-xs text-white/40">Placed {new Date(order.created_at).toLocaleDateString()}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
